import { FC } from 'react'
import { Box } from '@mui/material'
import {
  Edit,
  required,
  SimpleForm,
  TextInput,
  SelectInput,
  useRecordContext,
  useTranslate,
} from 'react-admin'
import { ConfirmDeleteToolbar } from '../../components'
import { FormSection, PageHeader, formSurfaceSx, pageSx } from '../../components/ui'
import { CertificateOut, CertificateUpdate } from '../../types/apiRequests'
import { CERTIFICATE_WRITE_SCOPE } from '../../config'
import { COMMON_NAME_EDITABLE_STATUSES, getCapabilities, targetSystemChoices, typeChoices } from './targetSystems'
import { hostnameValidation } from '../../utils/validation'
import { getCommonName } from '../../utils/getCommonName'

// Hostname is user-owned for manual systems, until the CSR is downloaded.
// Mirrors the API: a missing hostname is always editable, to repair legacy records.
const canEditCommonName = (record?: CertificateOut): boolean => {
  if (!record || getCapabilities(record.targetSystem)?.commonName !== 'manual') return false

  const hasEditableStatus = COMMON_NAME_EDITABLE_STATUSES.includes(record.status)
  const hasMissingHostname = !getCommonName(record)
  return hasEditableStatus || hasMissingHostname
}

// PUT only editable fields; targetSystem/type are fixed after create.
// metaData is replaced as a whole by the API, so keep its other keys.
const transform = (data: CertificateOut, options?: { previousData?: CertificateOut }): CertificateUpdate => {
  const update: CertificateUpdate = {
    contactNaam: data.contactNaam,
    contactEmail: data.contactEmail,
    contactTelefoon: data.contactTelefoon,
  }
  if (canEditCommonName(options?.previousData)) {
    update.metaData = {
      ...options?.previousData?.metaData,
      commonName: (data.metaData as { commonName?: string } | null)?.commonName,
    }
  }
  return update
}

const CommonNameInput: FC = () => {
  const record = useRecordContext<CertificateOut>()
  if (!canEditCommonName(record)) return null
  return (
    <TextInput
      source="metaData.commonName"
      label="resources.certificates.fields.host"
      validate={[required(), ...hostnameValidation]}
      fullWidth
    />
  )
}

const CertificateEdit: FC = () => {
  const translate = useTranslate()

  return (
    <Edit
      component="div"
      actions={false}
      redirect="show"
      transform={transform}
      sx={pageSx}
    >
      <PageHeader
        crumbs={[{ label: translate('resources.certificates.list'), to: '/certificate' }]}
        title={translate('resources.certificates.edit')}
      />
      {/* Surface wraps the form so sections and toolbar share one rounded card. */}
      <Box sx={formSurfaceSx}>
        <SimpleForm toolbar={<ConfirmDeleteToolbar access={[CERTIFICATE_WRITE_SCOPE]} />}>
          <FormSection
            title={translate('resources.certificates.sections.certificate')}
            description={translate('resources.certificates.sections.certificateHelp')}
          >
            <SelectInput
              source="targetSystem"
              label="resources.certificates.fields.targetSystem"
              choices={targetSystemChoices}
              readOnly
              fullWidth
            />

            <SelectInput
              source="type"
              label="resources.certificates.fields.type"
              choices={typeChoices}
              readOnly
              fullWidth
            />

            <CommonNameInput />
          </FormSection>

          <FormSection
            title={translate('resources.certificates.sections.contact')}
            description={translate('resources.certificates.sections.contactHelp')}
          >
            <TextInput source="contactNaam" label="data.contactpersoon" validate={[required()]} fullWidth />
            <TextInput source="contactEmail" type="email" label="data.email" validate={[required()]} fullWidth />
            <TextInput source="contactTelefoon" label="data.phone" validate={[required()]} fullWidth />
          </FormSection>
        </SimpleForm>
      </Box>
    </Edit>
  )
}

export default CertificateEdit
